/**
 * Scrollposition beim Seitenwechsel per Link (Bedienprüfung 28.10.2026): die Zielseite öffnete bisher dort, wo die
 * vorige gescrollt war — ein Link „→ Direkte Bestell-Links" auf /auswertungen/hebel landete mitten auf /kanaele, die
 * genannte Karte 1.939 px oberhalb des Bildes.
 *
 * - Neue Seite per Link (PUSH/REPLACE mit anderem Pfad): nach oben.
 * - Mit `#anker`: zur Karte mit dieser id, sobald sie im DOM steht, und nachgezogen, solange darüber liegende Karten
 *   beim Laden noch wachsen (bis ~8 s, Abbruch bei eigener Bedienung).
 * - Zurück/Vor (POP) und reine Query-Änderungen (Filter, Suche) lassen die Position in Ruhe.
 */
import { FC, useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const SeitenwechselScroll: FC = () => {
  const { pathname, hash } = useLocation();
  const typ = useNavigationType();
  const vorher = useRef(pathname);

  useEffect(() => {
    const neueSeite = vorher.current !== pathname;
    vorher.current = pathname;
    if (typ === 'POP') return undefined;
    if (hash.length > 1) {
      const id = decodeURIComponent(hash.slice(1));
      const timer: ReturnType<typeof setTimeout>[] = [];
      // Karten darüber laden ihre Daten nach und schieben das Ziel nach unten: bis die Seite ruht (höchstens ~8 s)
      // immer wieder hinspringen — außer der Mensch scrollt selbst.
      let eingegriffen = false;
      const halt = () => {
        eingegriffen = true;
      };
      const ereignisse = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;
      ereignisse.forEach((e) => window.addEventListener(e, halt, { passive: true }));
      let versuche = 0;
      const ziel = () => {
        if (eingegriffen) return;
        const el = document.getElementById(id);
        if (el) {
          const oben = el.getBoundingClientRect().top;
          if (Math.abs(oben - 16) > 4) el.scrollIntoView({ block: 'start' });
        }
        if (versuche++ < 80) timer.push(setTimeout(ziel, 100));
      };
      ziel();
      return () => {
        timer.forEach(clearTimeout);
        ereignisse.forEach((e) => window.removeEventListener(e, halt));
      };
    }
    if (neueSeite) window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash, typ]);

  return null;
};

export default SeitenwechselScroll;
